import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { gradeOfferingsApi, sectionsApi } from '@/services/api/endpoints/academic';
import { enrollmentsApi } from '@/services/api/endpoints/students';
import { queryKeys } from '@/services/api/queryKeys';
import type { PromotionResult } from '@/types/domain';

/**
 * Promoting a class into the next academic year.
 *
 * The screen is built around the preview because a promotion is irreversible in
 * practice: it closes thirty enrolments and opens thirty more. Showing exactly
 * who will move, and who cannot and why, before anything is written is what
 * makes that safe to do.
 *
 * Blocked students do not stop the batch. A class of thirty almost always
 * contains someone who has withdrawn or is repeating, and refusing the whole
 * group for one of them would make the feature unusable.
 */
export function PromotionPage() {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [sourceOfferingId, setSourceOfferingId] = useState('');
  const [targetOfferingId, setTargetOfferingId] = useState('');
  const [targetSectionId, setTargetSectionId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [result, setResult] = useState<PromotionResult | null>(null);

  const offerings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ size: 200 }),
    queryFn: () => gradeOfferingsApi.list({ page: 0, size: 200 }),
  });

  /** The class being promoted out of. */
  const enrollments = useQuery({
    queryKey: queryKeys.enrollments.list({ gradeOfferingId: sourceOfferingId, status: 'ACTIVE' }),
    queryFn: () =>
      enrollmentsApi.list({ page: 0, size: 200, gradeOfferingId: sourceOfferingId, status: 'ACTIVE' }),
    enabled: sourceOfferingId !== '',
  });

  const targetSections = useQuery({
    queryKey: queryKeys.sections.list({ gradeOfferingId: targetOfferingId }),
    queryFn: () => sectionsApi.list({ page: 0, size: 100, gradeOfferingId: targetOfferingId }),
    enabled: targetOfferingId !== '',
  });

  const enrollmentIds = useMemo(
    () => enrollments.data?.data.map((enrollment) => enrollment.id) ?? [],
    [enrollments.data],
  );

  const ready =
    sourceOfferingId !== '' &&
    targetOfferingId !== '' &&
    effectiveDate !== '' &&
    enrollmentIds.length > 0;

  const promote = useMutation({
    mutationFn: (preview: boolean) =>
      enrollmentsApi.promoteAll({
        enrollmentIds,
        targetGradeOfferingId: targetOfferingId,
        targetSectionId: targetSectionId === '' ? undefined : targetSectionId,
        effectiveDate,
        preview,
      }),
    onSuccess: (outcome) => {
      setResult(outcome);
      if (!outcome.preview) {
        toast.success(`Promoted ${outcome.eligibleCount} of ${enrollmentIds.length} students`);
        void queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
        void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
      }
    },
    onError: (error) => handleApiError(error),
  });

  /* A preview is stale the moment the selection changes, and acting on a stale
     one would promote a different set than the one reviewed. */
  const resetPreview = () => setResult(null);

  const offeringLabel = (id: string) => {
    const offering = offerings.data?.data.find((entry) => entry.id === id);
    return offering === undefined
      ? ''
      : `${offering.academicYear.name} · ${offering.grade.name}${offering.campusName !== undefined ? ` · ${offering.campusName}` : ''}`;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Promotion"
        description="Move a class into the next academic year. Last year's enrolment is closed and kept, never overwritten."
      />

      <Card>
        <CardHeader title="Choose the class and destination" />
        <div className="p-5">

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Promote from"
            required
            value={sourceOfferingId}
            onChange={(event) => {
              setSourceOfferingId(event.target.value);
              resetPreview();
            }}
          >
            <option value="">Select a class…</option>
            {offerings.data?.data.map((offering) => (
              <option key={offering.id} value={offering.id}>
                {offeringLabel(offering.id)}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Promote into"
            required
            value={targetOfferingId}
            onChange={(event) => {
              setTargetOfferingId(event.target.value);
              setTargetSectionId('');
              resetPreview();
            }}
            hint="Must be a different academic year — moving within a year is a transfer"
          >
            <option value="">Select a class…</option>
            {offerings.data?.data
              .filter((offering) => !offering.historical && offering.id !== sourceOfferingId)
              .map((offering) => (
                <option key={offering.id} value={offering.id}>
                  {offeringLabel(offering.id)}
                </option>
              ))}
          </SelectField>

          <SelectField
            label="Section"
            value={targetSectionId}
            onChange={(event) => {
              setTargetSectionId(event.target.value);
              resetPreview();
            }}
            hint="Optional — sections can be assigned later"
            disabled={targetOfferingId === ''}
          >
            <option value="">Assign later</option>
            {targetSections.data?.data.map((section) => (
              <option key={section.id} value={section.id}>
                {section.name}
              </option>
            ))}
          </SelectField>

          <TextField
            label="Effective date"
            type="date"
            required
            value={effectiveDate}
            onChange={(event) => {
              setEffectiveDate(event.target.value);
              resetPreview();
            }}
            hint="The new enrolment starts here; the old one ends the day before"
          />
        </div>

        {sourceOfferingId !== '' && (
          <p className="mt-4 text-sm text-muted">
            {enrollments.isFetching
              ? 'Counting students…'
              : `${enrollmentIds.length} active ${enrollmentIds.length === 1 ? 'student' : 'students'} in this class.`}
          </p>
        )}

        <div className="mt-5 flex gap-2">
          <Button
            variant="secondary"
            disabled={!ready}
            loading={promote.isPending && result === null}
            onClick={() => promote.mutate(true)}
          >
            Preview
          </Button>
          <Button
            disabled={!ready || result === null || result.preview === false}
            loading={promote.isPending && result !== null}
            onClick={() => promote.mutate(false)}
          >
            Promote {result?.eligibleCount ?? 0} students
          </Button>
        </div>

        {result === null && ready && (
          <p className="mt-3 text-xs text-subtle">
            Preview first — it reports exactly who will move without writing anything.
          </p>
        )}
        </div>
      </Card>

      {result !== null && (
        <Card>
          <CardHeader
            title={result.preview ? 'Preview' : 'Result'}
            description={
              result.preview
                ? 'Nothing has been written yet.'
                : 'These students have been promoted.'
            }
            action={
              <div className="flex gap-2">
                <Badge tone="success">{result.eligibleCount} eligible</Badge>
                {result.blockedCount > 0 && <Badge tone="warning">{result.blockedCount} blocked</Badge>}
              </div>
            }
          />

          <ul className="divide-y divide-line">
            {result.outcomes.map((outcome) => (
              <li key={outcome.enrollmentId} className="flex items-start gap-3 px-5 py-2.5">
                {outcome.eligible ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                ) : (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
                )}
                <div className="min-w-0">
                  <p className="text-sm text-ink">
                    {outcome.studentName ?? 'Unknown student'}
                    {outcome.studentNumber !== undefined && (
                      <span className="ml-2 font-mono text-xs text-subtle">
                        {outcome.studentNumber}
                      </span>
                    )}
                  </p>
                  {outcome.blockedReason !== undefined && (
                    <p className="mt-0.5 text-xs text-warning">{outcome.blockedReason}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {enrollments.error !== null && sourceOfferingId !== '' && (
        <ErrorState error={enrollments.error} onRetry={() => void enrollments.refetch()} />
      )}
      {offerings.isPending && <LoadingState label="Loading classes" />}
    </div>
  );
}
